import { GetHomePageDataQuery } from 'generated/graphql';
import { PATHS } from '@client/routes';
import auth from '@client/components/auth';
import { toProgramLandingPageUrl } from '@client/helpers/routes';
import { Collection, Collections } from './home.domain';
import { HomeVMVDispatcher } from './home.viewModel';
import {
  faEye,
  faEyeSlash,
  faMobile,
  faMoneyBillTransfer,
  faUnlock,
} from '@fortawesome/pro-regular-svg-icons';
import { faRocket } from '@fortawesome/pro-solid-svg-icons';
import { ButtonColor } from '@design-system/components/button/button.css';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export type Feedback = Array<{
  id: number;
  name: string;
  thumbnail: string;
  rating: number;
  workoutName: string;
  text: string;
  createdAt: number;
}>;

export type CreatorLinksType = Array<{
  title: string;
  url: string;
}>;

export const toFeedbackData = (
  data: GetHomePageDataQuery['searchComments'],
): Feedback => {
  return data.map((comment) => ({
    id: comment.id,
    name: comment.user?.name || '',
    thumbnail:
      `${comment.user?.mediaThumbnail?.cdn01}${comment.user?.mediaThumbnail?.image}` ||
      '',
    rating: comment.userActivity?.floatValue || 0,
    workoutName: comment.workout?.name || '',
    text: comment.textValue,
    createdAt: Number(comment.createdAt),
  }));
};

export const getChecklistItems = ({
  data,
  creatorId,
  email,
  isLive,
  dispatch,
}: {
  data?: {
    profileReady: boolean;
    paymentMethodReady: boolean;
    collectionPublishedReady: boolean;
  };
  creatorId?: number;
  email?: string;
  isLive: boolean;
  dispatch: HomeVMVDispatcher;
}) => {
  if (!data || !creatorId || isLive) return [];
  const { profileReady, paymentMethodReady, collectionPublishedReady } = data;

  return [
    {
      title: 'Complete profile',
      done: profileReady,
      to: getEditProfileLink(creatorId, email),
    },
    {
      title: 'Set up Stripe',
      done: paymentMethodReady,
      to: getPaymentLink(creatorId),
    },
    {
      title: 'Setup offer',
      done: collectionPublishedReady,
      onClick: () => dispatch('setupOffer'),
    },
  ];
};

export const getEditProfileLink = (creatorId: number, email?: string) => {
  if (!email) {
    return auth.isInfluencer()
      ? PATHS.CREATOR.SETTINGS
      : PATHS.PARTNER_SETTINGS(creatorId);
  }
  return auth.isInfluencer()
    ? PATHS.CREATOR.PROFILE(true)
    : PATHS.PARTNER_EDIT(creatorId);
};

const getPaymentLink = (creatorId: number) => {
  return auth.isInfluencer()
    ? PATHS.ADMIN_PAYMENT(creatorId)
    : PATHS.ADMIN_ADMINISTRATION(creatorId);
};

export const getSubscriptionLinks = (
  isPPC: boolean,
  customURL?: string,
  profileDeepLink?: string,
) => {
  const landingPageLink = customURL
    ? `${import.meta.env.VITE_LANDING_PAGE_URL}/${customURL}`
    : '';

  const baseLinks = [
    {
      title: 'Subscription landing page',
      url: landingPageLink,
    },
    {
      title: 'Playbook app channel',
      url: profileDeepLink ?? '',
    },
    {
      title: 'Subscription checkout page',
      url: landingPageLink ? `${landingPageLink}/checkout` : '',
    },
  ];

  const additionalLinks = [
    {
      title: 'Gifting page',
      url: landingPageLink ? `${landingPageLink}/gift` : '',
    },
  ];
  return isPPC ? baseLinks : [...baseLinks, ...additionalLinks];
};

export const getOtpLinks = ({
  customURL,
  selectedOTP,
}: {
  selectedOTP?: Collection;
  customURL?: string;
}): CreatorLinksType => {
  if (!customURL || !selectedOTP) return [];

  const landingPageLink = toProgramLandingPageUrl({
    id: selectedOTP.id,
    name: selectedOTP.name,
    influencer: { customURL },
  });
  return [
    {
      title: 'Program web page',
      url: landingPageLink,
    },
    {
      title: 'Program checkout page',
      url: `${landingPageLink}/checkout`,
    },
    {
      title: 'Program in-app page',
      url:
        selectedOTP.exclusiveProgramMetadata?.deeplinkUrl || 'Link not found',
    },
  ];
};

export const getHomeCta = ({
  isLive,
  dispatch,
  canGoLive,
}: {
  isLive: boolean;
  dispatch: HomeVMVDispatcher;
  canGoLive: boolean;
}) => {
  if (isLive) return;

  return [
    {
      icon: faRocket,
      content: 'Go live',
      onClick: () => dispatch('openGoLiveModal'),
      disabled: !canGoLive,
      color: 'success' as ButtonColor,
    },
    {
      icon: faMobile,
      onClick: () => dispatch('openQrCodeModal'),
    },
  ];
};

type RawCollections = GetHomePageDataQuery['collections'];

export const getOTPPrograms = (collections: RawCollections): Collections => {
  if (!collections) return [];

  return collections
    .filter(
      (i) =>
        i.accessType === 'exclusive' &&
        i.publishedAt !== null &&
        i.archivedAt === null &&
        i.name !== null,
    )
    .map((i) => ({
      ...i,
      name: i.name || 'not found',
      exclusiveProgramMetadata: i.exclusiveProgramMetadata || null,
      publishedAt: i.publishedAt || null,
      archivedAt: i.archivedAt || null,
    }));
};

export const getSubscriptionPrograms = (
  collections: RawCollections,
): Collections => {
  if (!collections) return [];

  return collections
    .filter(
      (i) =>
        i.accessType === 'premium' &&
        i.publishedAt !== null &&
        i.archivedAt === null &&
        i.name !== null,
    )
    .map((i) => ({
      ...i,
      name: i.name || 'not found',
      exclusiveProgramMetadata: i.exclusiveProgramMetadata || null,
      publishedAt: i.publishedAt || null,
      archivedAt: i.archivedAt || null,
    }));
};

export type SubscriptionTypeOptions = Array<{
  title: string;
  type: 'platform' | 'ppc';
  details: string;
  tag?: {
    label: string;
    className?: string;
    readonly: boolean;
  };
  list: Array<{ icon: IconProp; title: string; details: string }>;
  onClick: () => void;
  disabled?: boolean;
}>;

export const toSubscriptionEmptyStateOptionsByMode = ({
  onSelect,
  disabledType,
}: {
  onSelect: (type: 'ppc' | 'platform') => void;
  disabledType?: 'platform' | 'ppc';
}): SubscriptionTypeOptions => [
  {
    title: 'Platform Subscription',
    type: 'platform',
    details: 'Share your content with a wider audience',
    list: [
      {
        icon: faMoneyBillTransfer,
        title: 'Pricing',
        details: 'Charge  $14.99/month or $99/year subscription fee.',
      },
      {
        icon: faEye,
        title: 'Who can access your content',
        details:
          'People who discover you on the platform and are Playbook subscribers',
      },
      {
        icon: faUnlock,
        title: 'Discoverability',
        details:
          'Your subscribers can access other platform creators for free.',
      },
    ],
    onClick: () => onSelect('platform'),
    disabled: disabledType === 'platform',
  },
  {
    title: 'Exclusive Subscription',
    type: 'ppc',
    tag: {
      label: 'BETA',
      className: 'bg-[#DA6A02] text-white h-[26px]',
      readonly: true,
    },
    details: 'Gated subscription where you pick your price',
    list: [
      {
        icon: faMoneyBillTransfer,
        title: 'Pricing',
        details: 'You can charge any price you think your content is worth.',
      },
      {
        icon: faEyeSlash,
        title: 'Who can access your content',
        details:
          "Only people with your link and your subscription. You won't be discoverable by others.",
      },
      {
        icon: faUnlock,
        title: 'Discoverability',
        details:
          'Your subscribers cannot access other platform creators for free.',
      },
    ],
    onClick: () => onSelect('ppc'),
    disabled: disabledType === 'ppc',
  },
];
